import type { $Fetch } from 'ofetch';
import type { Preco, PrecoCommand } from '@/domain/model/Preco';
import type { PrecoMeioPagamento, PrecoMeioCommand } from '@/domain/model/PrecoMeio';
import type { Page } from '@/domain/model/Page';
import type { PrecoGateway } from '@/application/port/out/PrecoGateway';
import type { ApiResponse, SpringPage } from '@/infrastructure/http/types';
import { assertApiSuccess } from '@/infrastructure/http/assertApiSuccess';

const PATH = '/api/v1/commerce/precos';

export class HttpPrecoGateway implements PrecoGateway {
  constructor(private readonly client: $Fetch) {}

  async list(p: { ativo?: boolean; page?: number; size?: number }): Promise<Page<Preco>> {
    const params: Record<string, string | number | boolean> = {};
    if (typeof p.ativo === 'boolean') params['ativo'] = p.ativo;
    if (typeof p.page === 'number') params['page'] = p.page;
    if (typeof p.size === 'number') params['size'] = p.size;
    const res = await this.client<ApiResponse<SpringPage<Preco>>>(PATH, { method: 'GET', params });
    assertApiSuccess(res);
    const d = res.data;
    return { content: d.content, number: d.number, size: d.size, totalElements: d.totalElements, totalPages: d.totalPages, first: d.first, last: d.last };
  }

  async get(id: string): Promise<Preco> {
    const res = await this.client<ApiResponse<Preco>>(`${PATH}/${encodeURIComponent(id)}`, { method: 'GET' });
    assertApiSuccess(res);
    return res.data;
  }

  async criar(command: PrecoCommand): Promise<Preco> {
    const res = await this.client<ApiResponse<Preco>>(PATH, { method: 'POST', body: command });
    assertApiSuccess(res);
    return res.data;
  }

  async atualizar(id: string, command: PrecoCommand): Promise<Preco> {
    const res = await this.client<ApiResponse<Preco>>(
      `${PATH}/${encodeURIComponent(id)}`, { method: 'PUT', body: command },
    );
    assertApiSuccess(res);
    return res.data;
  }

  async inativar(id: string): Promise<void> {
    await this.client<null>(`${PATH}/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }

  async listMeios(precoId: string): Promise<PrecoMeioPagamento[]> {
    const res = await this.client<ApiResponse<PrecoMeioPagamento[]>>(
      `${PATH}/${encodeURIComponent(precoId)}/meios`, { method: 'GET' });
    assertApiSuccess(res);
    return res.data;
  }

  async addMeio(precoId: string, command: PrecoMeioCommand): Promise<PrecoMeioPagamento> {
    const res = await this.client<ApiResponse<PrecoMeioPagamento>>(
      `${PATH}/${encodeURIComponent(precoId)}/meios`, { method: 'POST', body: command });
    assertApiSuccess(res);
    return res.data;
  }

  async removeMeio(precoId: string, meioId: string): Promise<void> {
    // DELETE 204 No Content.
    await this.client<null>(
      `${PATH}/${encodeURIComponent(precoId)}/meios/${encodeURIComponent(meioId)}`, { method: 'DELETE' });
  }
}
